import { useGridUserDataGlobalFiltersContext } from 'contexts/GridUserDataGlobalFiltersProvider';
import React, { createContext, useContext, useCallback, useRef, useEffect, useState } from 'react';
import { logScannerAction } from 'utils/scanner/logScannerAction';
import { useSPPScanner, LAST_KNOWN_PORT_INDEX_KEY } from './useSPPScanner';

const SPPScannerContext = createContext(null);

export const SCANNER_DEVICE_AUTHORIZED_KEY = 'spp_scanner_device_authorized'; // User has authorized a device

export const SPPScannerProvider = ({ children }) => {
    // Has user authorized a device (clicked CONNECT and selected device)
    const [isDeviceAuthorized, setIsDeviceAuthorized] = useState(() => {
        const stored = localStorage.getItem(SCANNER_DEVICE_AUTHORIZED_KEY);
        return stored === 'true';
    });

    // Track active target pages
    const [activeTargetPages, setActiveTargetPages] = useState(new Set());

    const [isReconnecting, setIsReconnecting] = useState(false);
    const scanHandlersRef = useRef({});
    const reconnectIntervalRef = useRef(null);
    const isDeviceAuthorizedRef = useRef(isDeviceAuthorized);
    const activeTargetPagesRef = useRef(activeTargetPages);
    const scannerHookRef = useRef(null);
    const isManualAuthorizationRef = useRef(false);

    // Terminal for the scan log comes from the global terminal filter; kept in a ref for the stable scan handler.
    const { data: globalFilters = {} } = useGridUserDataGlobalFiltersContext();
    const terminalIdRef = useRef(null);
    terminalIdRef.current = globalFilters?.terminal_id?.[0] ?? null;

    // Per-target-page scan-log context ({ contextType, contextId, terminalId }); empty = misc + global terminal.
    const scanContextsRef = useRef({});

    // Keep refs in sync with state
    useEffect(() => {
        isDeviceAuthorizedRef.current = isDeviceAuthorized;
    }, [isDeviceAuthorized]);

    useEffect(() => {
        activeTargetPagesRef.current = activeTargetPages;
    }, [activeTargetPages]);

    const handleConnectionChange = useCallback((connected, isManualAuth = false) => {
        if (connected) {
            setIsReconnecting(false);
            if (reconnectIntervalRef.current) {
                clearInterval(reconnectIntervalRef.current);
                reconnectIntervalRef.current = null;
            }
        }
        // Don't trigger notifications during manual authorization
        isManualAuthorizationRef.current = isManualAuth;
    }, []);

    // Check if we should be connected (device authorized + on target page)
    const shouldBeConnected = useCallback(() => {
        return isDeviceAuthorizedRef.current && activeTargetPagesRef.current.size > 0;
    }, []);

    const startAutoReconnect = useCallback(() => {
        // Only start auto-reconnect if we should be connected
        if (!shouldBeConnected()) {
            console.log('Not starting auto-reconnect - not on target page or device not authorized');
            return;
        }

        // Clear any existing reconnect attempts
        if (reconnectIntervalRef.current) {
            clearInterval(reconnectIntervalRef.current);
        }

        setIsReconnecting(true);

        // Try to reconnect every 3 seconds
        reconnectIntervalRef.current = setInterval(async () => {
            if (shouldBeConnected() && scannerHookRef.current && !scannerHookRef.current.isConnected) {
                console.log('Attempting auto-reconnect...');
                try {
                    await scannerHookRef.current.connect(true); // silentReconnect = true
                } catch (error) {
                    console.log('Auto-reconnect attempt failed, will retry...', error.message);
                }
            } else if (!shouldBeConnected()) {
                // Stop trying if we shouldn't be connected
                console.log('Stopping auto-reconnect - no longer on target page');
                if (reconnectIntervalRef.current) {
                    clearInterval(reconnectIntervalRef.current);
                    reconnectIntervalRef.current = null;
                }
                setIsReconnecting(false);
            } else if (scannerHookRef.current && scannerHookRef.current.isConnected) {
                // Connected successfully
                console.log('Auto-reconnect successful, stopping interval');
                if (reconnectIntervalRef.current) {
                    clearInterval(reconnectIntervalRef.current);
                    reconnectIntervalRef.current = null;
                }
                setIsReconnecting(false);
            }
        }, 3000);
    }, [shouldBeConnected]);

    const handleDeviceOffline = useCallback(() => {
        if (shouldBeConnected()) {
            startAutoReconnect();
        } else {
            setIsReconnecting(false);
        }
    }, [shouldBeConnected, startAutoReconnect]);

    const scannerHook = useSPPScanner({
        onConnectionChange: handleConnectionChange,
        onDeviceOffline: handleDeviceOffline,
    });

    // Keep scannerHook ref in sync
    scannerHookRef.current = scannerHook;

    // User clicks CONNECT - request device authorization
    // Returns true if authorized, false if user cancelled
    const requestDeviceAuthorization = useCallback(async () => {
        try {
            isManualAuthorizationRef.current = true;
            // This will show browser dialog to select device
            await scannerHook.connect();
            // If successful, mark device as authorized
            setIsDeviceAuthorized(true);
            localStorage.setItem(SCANNER_DEVICE_AUTHORIZED_KEY, 'true');
            // Immediately disconnect after authorization (we'll reconnect on target pages)
            await scannerHook.disconnect();
            isManualAuthorizationRef.current = false;
            return true;
        } catch (error) {
            isManualAuthorizationRef.current = false;
            // User cancelled device selection - not an error, just return false
            if (error.name === 'NotFoundError' || error.message?.includes('User cancelled')) {
                return false;
            }
            // Connection failed (device offline, etc.) - throw so UI can show error
            console.error('Failed to authorize device:', error);
            throw error;
        }
    }, [scannerHook]);

    // User clicks DISCONNECT/FORGET - forget device authorization
    const forgetDeviceAuthorization = useCallback(async () => {
        setIsDeviceAuthorized(false);
        localStorage.setItem(SCANNER_DEVICE_AUTHORIZED_KEY, 'false');
        // Drop remembered port priority
        localStorage.removeItem(LAST_KNOWN_PORT_INDEX_KEY);
        setIsReconnecting(false);

        // Stop reconnection attempts
        if (reconnectIntervalRef.current) {
            clearInterval(reconnectIntervalRef.current);
            reconnectIntervalRef.current = null;
        }

        // Disconnect if currently connected
        if (scannerHook.isConnected) {
            await scannerHook.disconnect();
        }
    }, [scannerHook]);

    // Register a target page (PALLETS or PICKING LIST)
    const registerTargetPage = useCallback((pageId, context = null) => {
        scanContextsRef.current[pageId] = context;
        setActiveTargetPages(prev => {
            const newSet = new Set(prev);
            newSet.add(pageId);
            return newSet;
        });

        // Return unregister function
        return () => {
            delete scanContextsRef.current[pageId];
            setActiveTargetPages(prev => {
                const newSet = new Set(prev);
                newSet.delete(pageId);
                return newSet;
            });
        };
    }, []);

    // Auto-connect/disconnect based on page presence and device authorization
    useEffect(() => {
        const shouldConnect = shouldBeConnected();

        if (shouldConnect && !scannerHook.isConnected && !isReconnecting) {
            // Should be connected but not connected - start connecting
            console.log('Target page mounted and device authorized - auto-connecting...');
            scannerHook.connect(true).catch(() => { // silentReconnect = true for auto-connect
                // If connection fails, start auto-reconnect
                startAutoReconnect();
            });
        } else if (!shouldConnect && scannerHook.isConnected) {
            // Should not be connected but connected - disconnect
            console.log('No target pages - auto-disconnecting...');
            scannerHook.disconnect();
            if (reconnectIntervalRef.current) {
                clearInterval(reconnectIntervalRef.current);
                reconnectIntervalRef.current = null;
            }
            setIsReconnecting(false);
        }
    }, [activeTargetPages, isDeviceAuthorized, shouldBeConnected, scannerHook, isReconnecting, startAutoReconnect]);

    // Register scan handler for a specific page/component
    const registerScanHandler = useCallback((handlerId, handler) => {
        if (handler) {
            scanHandlersRef.current[handlerId] = handler;
        }
        return () => {
            delete scanHandlersRef.current[handlerId];
        };
    }, []);

    // Global scan handler that routes to active handlers
    const globalScanHandler = useCallback((code) => {
        // Log with the active target page's context; fall back to misc + the global terminal filter.
        const context = Object.values(scanContextsRef.current).find(Boolean) || null;
        logScannerAction(code, {
            scannerName: scannerHookRef.current?.deviceLabel,
            contextType: context?.contextType,
            contextId: context?.contextId,
            terminalId: context?.terminalId ?? terminalIdRef.current,
        });
        Object.values(scanHandlersRef.current).forEach(handler => {
            if (typeof handler === 'function') {
                handler(code);
            }
        });
    }, []);

    // Register the global handler with the scanner
    useEffect(() => {
        scannerHook.registerScanHandler(globalScanHandler);
    }, [scannerHook.registerScanHandler, globalScanHandler]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (reconnectIntervalRef.current) {
                clearInterval(reconnectIntervalRef.current);
            }
        };
    }, []);

    const contextValue = {
        isConnected: scannerHook.isConnected,
        error: scannerHook.error,
        requestDeviceAuthorization,
        forgetDeviceAuthorization,
        isDeviceAuthorized,
        registerScanHandler,
        registerTargetPage,
        isReconnecting,
        isManualAuthorizationRef,
    };

    return (
        <SPPScannerContext.Provider value={contextValue}>
            {children}
        </SPPScannerContext.Provider>
    );
};

export const useSPPScannerContext = () => {
    const context = useContext(SPPScannerContext);
    if (!context) {
        throw new Error('useSPPScannerContext must be used within SPPScannerProvider');
    }
    return context;
};
